import { useCallback, useState } from "react";

import {
    createMenuItemBadge,
    deleteMenuItemBadge as deleteMenuItemBadgeRequest,
    updateMenuItemBadge,
} from "services/menuItemBadges/rest";
import { MenuItemBadgeDto } from "services/menuItemBadges/restModels";
import { MenuItemBadgeRequestDto } from "services/menuItemBadges/restRequests";
import { UseNotificationSnackbarReturn } from "shared/ui";

export interface UseMenuItemBadgeMutationsResult {
    saving: boolean;
    saveBadge: (
        values: MenuItemBadgeRequestDto,
        menuItemBadgeId: number | null
    ) => Promise<MenuItemBadgeDto | null>;
    removeBadge: (menuItemBadgeId: number) => Promise<MenuItemBadgeDto[] | null>;
}

export const useMenuItemBadgeMutations = (
    badges: MenuItemBadgeDto[],
    setBadges: (next: MenuItemBadgeDto[]) => void,
    notify: UseNotificationSnackbarReturn
): UseMenuItemBadgeMutationsResult => {
    const [saving, setSaving] = useState<boolean>(false);

    const saveBadge = useCallback(
        async (
            values: MenuItemBadgeRequestDto,
            menuItemBadgeId: number | null
        ): Promise<MenuItemBadgeDto | null> => {
            setSaving(true);
            try {
                if (menuItemBadgeId) {
                    const updated = await updateMenuItemBadge(menuItemBadgeId, values);

                    if (updated) {
                        setBadges(badges.map((item) => (item.id === updated.id ? updated : item)));
                    }

                    notify.showSuccess("Бейдж сохранён");
                    return updated;
                }

                const created = await createMenuItemBadge(values);

                // Порядок реестра — порядок создания, поэтому новый бейдж дописываем в конец.
                if (created) setBadges([...badges, created]);

                notify.showSuccess("Бейдж добавлен");
                return created;
            } catch (e) {
                notify.showError("Не удалось сохранить бейдж", e);
                return null;
            } finally {
                setSaving(false);
            }
        },
        [badges, notify, setBadges]
    );

    const removeBadge = useCallback(
        async (menuItemBadgeId: number): Promise<MenuItemBadgeDto[] | null> => {
            setSaving(true);
            try {
                const result = await deleteMenuItemBadgeRequest(menuItemBadgeId);

                if (result) setBadges(result.badges);

                notify.showSuccess(
                    result && result.detachedFromItemsCount > 0
                        ? `Бейдж удалён и снят с позиций: ${result.detachedFromItemsCount}`
                        : "Бейдж удалён"
                );

                return result ? result.badges : null;
            } catch (e) {
                notify.showError("Не удалось удалить бейдж", e);
                return null;
            } finally {
                setSaving(false);
            }
        },
        [notify, setBadges]
    );

    return { saving, saveBadge, removeBadge };
};
